import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  ClipboardList,
  PenLine,
  CheckCircle2,
  MessageSquareHeart,
  CircleCheck,
  CircleSlash,
  Clock3,
  Wallet,
  CalendarDays,
  Lock,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { formatCurrency } from '@/lib/utils/currency';
import { useAuth } from '@/features/auth';
import { useChangeRequestMutations } from '../hooks';
import type { Matter } from '../types';

interface MatterPlanCardProps {
  matter: Matter;
}

/** Phases where the plan is still free-edit (pre-Gate-2). */
const FREE_EDIT_PHASES = ['INTAKE', 'PROVISIONAL_OPEN', 'PLANNING'];

/**
 * Case 360° (Lot 2) Stage 11 — the Matter Plan: scope in/out, estimate,
 * budget, target end date + the two soft-check flags (plan confirmed → Gate 2,
 * feedback requested → Gate 4). Free-edit by Lead/Partner/MD only pre-Gate-2;
 * from EXECUTION on, the fields change only through an approved Change Request
 * (see ChangeRequestsCard).
 */
export function MatterPlanCard({ matter }: MatterPlanCardProps) {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuth();
  const { updatePlanMutation, confirmPlanMutation, requestFeedbackMutation } =
    useChangeRequestMutations();

  // Optimistic client-side gates — the server re-checks everything.
  const globalRoleType = user?.role?.type;
  const isTeamPartner =
    matter?.partners?.some((p) => p?.id === user?.id) ?? false;
  const isTeamLead =
    matter?.responsibleAttorneys?.some((a) => a?.id === user?.id) ?? false;
  // `matter:manage-all` = firm-wide case-management scope (Office Manager);
  // the server-side gates accept it alongside the senior role types.
  const isPlanController =
    globalRoleType === 'MD' ||
    globalRoleType === 'MANAGER' ||
    hasAccess('matter', 'manage-all') ||
    isTeamPartner ||
    isTeamLead;

  const phase = matter?.phase ?? 'INTAKE';
  const isFreeEdit = FREE_EDIT_PHASES.includes(phase);
  const isClosed = phase === 'CLOSED';
  const canEdit =
    hasAccess('matter', 'update') && isPlanController && isFreeEdit && !isClosed;
  const canConfirm =
    hasAccess('matter', 'update') &&
    isPlanController &&
    !matter?.matterPlanConfirmed &&
    !isClosed;
  const canRequestFeedback =
    hasAccess('matter', 'update') &&
    isPlanController &&
    phase === 'REVIEW' &&
    !matter?.feedbackRequested;

  // --- edit dialog ---
  const [editOpen, setEditOpen] = React.useState(false);
  const [scopeIn, setScopeIn] = React.useState('');
  const [scopeOut, setScopeOut] = React.useState('');
  const [estimate, setEstimate] = React.useState('');
  const [budget, setBudget] = React.useState('');
  const [targetEndDate, setTargetEndDate] = React.useState('');

  const openEdit = () => {
    setScopeIn(matter?.scopeIn ?? '');
    setScopeOut(matter?.scopeOut ?? '');
    setEstimate(matter?.estimate != null ? String(matter.estimate) : '');
    setBudget(matter?.budget != null ? String(matter.budget) : '');
    setTargetEndDate(
      matter?.targetEndDate
        ? format(new Date(matter.targetEndDate), 'yyyy-MM-dd')
        : '',
    );
    setEditOpen(true);
  };

  const isPending = updatePlanMutation.isPending;

  const handleSave = async () => {
    if (!matter || isPending) return;
    await updatePlanMutation.mutateAsync({
      id: matter.id,
      data: {
        scopeIn,
        scopeOut,
        ...(estimate !== '' ? { estimate: Math.max(0, Number(estimate) || 0) } : {}),
        ...(budget !== '' ? { budget: Math.max(0, Number(budget) || 0) } : {}),
        ...(targetEndDate
          ? { targetEndDate: new Date(targetEndDate).toISOString() }
          : {}),
      },
    });
    setEditOpen(false);
  };

  const stats: { icon: React.ReactNode; label: string; value: string | null }[] = [
    {
      icon: <Clock3 className="h-4 w-4 text-slate-400" />,
      label: t('plan.estimate'),
      value:
        matter?.estimate != null ? `${matter.estimate} ${t('plan.hours')}` : null,
    },
    {
      icon: <Wallet className="h-4 w-4 text-slate-400" />,
      label: t('plan.budget'),
      // The budget (billing cap) is in the contract's currency.
      value:
        matter?.budget != null
          ? formatCurrency(matter.budget, {
              currency: matter?.contract?.currency ?? 'USD',
            })
          : null,
    },
    {
      icon: <CalendarDays className="h-4 w-4 text-slate-400" />,
      label: t('plan.targetEndDate'),
      value: matter?.targetEndDate
        ? format(new Date(matter.targetEndDate), 'dd MMM yyyy')
        : null,
    },
  ];

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-2">
            <ClipboardList className="h-5 w-5 text-slate-400" />
            {t('plan.cardTitle')}
            {matter?.matterPlanConfirmed && (
              <Badge className="border border-emerald-300 bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                {t('plan.confirmed')}
              </Badge>
            )}
            {matter?.feedbackRequested && (
              <Badge className="border border-sky-300 bg-sky-50 text-sky-700">
                <MessageSquareHeart className="mr-1 h-3 w-3" />
                {t('plan.feedbackRequestedBadge')}
              </Badge>
            )}
          </span>
          <span className="flex items-center gap-1">
            {canEdit && (
              <Button size="sm" variant="outline" onClick={openEdit}>
                <PenLine className="mr-1 h-3.5 w-3.5" />
                {t('edit')}
              </Button>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Scope — in vs out, side by side */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-emerald-700">
              <CircleCheck className="h-3.5 w-3.5" />
              {t('plan.scopeIn')}
            </p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
              {matter?.scopeIn || (
                <span className="text-slate-400">{t('plan.notDefined')}</span>
              )}
            </p>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-rose-700">
              <CircleSlash className="h-3.5 w-3.5" />
              {t('plan.scopeOut')}
            </p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
              {matter?.scopeOut || (
                <span className="text-slate-400">{t('plan.notDefined')}</span>
              )}
            </p>
          </div>
        </div>

        {/* Estimate / Budget / Target date tiles */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
            >
              <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {stat.icon}
                {stat.label}
              </p>
              <p
                className={`mt-1 text-sm font-semibold ${
                  stat.value ? 'text-slate-900' : 'text-slate-400'
                }`}
              >
                {stat.value ?? t('plan.notDefined')}
              </p>
            </div>
          ))}
        </div>

        {!isFreeEdit && !isClosed && (
          <p className="flex items-center gap-1.5 text-xs text-slate-400">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            {t('plan.lockedHint')}
          </p>
        )}

        {(canConfirm || canRequestFeedback) && (
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {canConfirm && (
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                disabled={confirmPlanMutation.isPending}
                onClick={() => confirmPlanMutation.mutate(matter.id)}
              >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                {t('plan.confirm')}
              </Button>
            )}
            {canRequestFeedback && (
              <Button
                size="sm"
                variant="outline"
                className="border-sky-300 text-sky-700 hover:bg-sky-50"
                disabled={requestFeedbackMutation.isPending}
                onClick={() => requestFeedbackMutation.mutate(matter.id)}
              >
                <MessageSquareHeart className="mr-1 h-3.5 w-3.5" />
                {t('plan.requestFeedback')}
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-slate-500" />
              {t('plan.editDialogTitle')}
            </DialogTitle>
            <DialogDescription>{t('plan.editDialogDescription')}</DialogDescription>
          </DialogHeader>

          <div className="my-2 space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('plan.scopeIn')}
              </label>
              <Textarea
                value={scopeIn}
                onChange={(e) => setScopeIn(e.target.value)}
                rows={2}
                maxLength={5000}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('plan.scopeOut')}
              </label>
              <Textarea
                value={scopeOut}
                onChange={(e) => setScopeOut(e.target.value)}
                rows={2}
                maxLength={5000}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('plan.estimate')}
                </label>
                <Input
                  type="number"
                  min={0}
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">
                  {t('plan.budget')} ({matter?.contract?.currency ?? 'USD'})
                </label>
                <Input
                  type="number"
                  min={0}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('plan.targetEndDate')}
              </label>
              <DateTimePicker
                mode="date"
                value={targetEndDate || undefined}
                onChange={(d) => setTargetEndDate(d ? format(d, 'yyyy-MM-dd') : '')}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={isPending}
            >
              {t('cancel')}
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? t('processing') : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
